/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { OperationStatus } from "Constants/OperationStatus";
import { AppState } from "Dataflows/Reducers";
import { IM365BenefitDetail } from "Interfaces/Benefits/IM365BenefitDetail";
import { IMicrosoft365BenefitDetail } from "Interfaces/Benefits/IMicrosoft365BenefitDetail";
import { IProfileBenefitResult } from "Interfaces/Benefits/IProfileBenefitResult";
import { ITeamsBenefitDetail } from "Interfaces/Benefits/ITeamsBenefitDetail";
import { IFilterValue } from "Interfaces/IFilterValue";
import { IOperationStatusType } from "Interfaces/IOperationStatusType";
import { IRDAzureBenefitClaim } from "Interfaces/RD/IRDAzureBenefitClaim";
import { IRDProgramProfile } from "Interfaces/RD/ProgramProfile/IRDProgramProfile";
import { IRDUserProfile } from "Interfaces/RD/UserProfile/IRDUserProfile";
import { IRDUserProfileDetail } from "Interfaces/RD/UserProfile/IRDUserProfileDetail";
import { IUserProfileComment } from "Interfaces/UserProfile/IUserProfileComment";

export const getState = (state: AppState): AppState => state;

/**
 * Returns current value from state for: currentPage
 * @param {AppState} state
 * @returns {number}
 */
export const getCurrentPage = (state: AppState): number => state.rdUserProfileState.currentPage;

/**
 * Returns current value from state for: pageSize
 * @param {AppState} state
 * @returns {number}
 */
export const getPageSize = (state: AppState): number => state.rdUserProfileState.pageSize;

/**
 * Returns current value from state for: searchText
 * @param {AppState} state
 * @returns {string}
 */
export const getSearchText = (state: AppState): string => state.rdUserProfileState.searchText;

/**
 * Returns the filtered count.
 * @param {AppState} state
 *  @returns {number}
 */
export const getFilteredCount = (state: AppState): number => state.rdUserProfileState.filteredCount;

/**
 * Returns current value from state for: isUserProfilesLoading
 * @param {AppState} state
 * @returns {boolean}
 */
export const isUserProfilesLoading = (state: AppState): boolean =>
  state.rdUserProfileState.isUserProfilesLoading;

/**
 * Returns current value from state for: userProfiles
 * @param {AppState} state
 * @returns {IRDUserProfile[]}
 */
export const getUserProfiles = (state: AppState): IRDUserProfile[] =>
  state.rdUserProfileState.userProfiles;

/**
 * Returns file down load data.
 * @param {AppState} state
 * @returns {string|undefined}
 */
export const getFileDownloadData = (state: AppState): string | undefined =>
  state.rdUserProfileState.fileDownloadData;

/**
 * Returns file down load status.
 * @param {AppState} state
 * @returns {OperationStatus}
 */
export const getFileDownloadStatus = (state: AppState): OperationStatus =>
  state.rdUserProfileState.fileDownloadStatus;

/**
 * Returns current value from state for: userProfiles
 * @param userProfileState
 * @returns {IRDUserProfile[]}
 */
export const getUserProfilesFilterValues = (state: AppState): IFilterValue[] =>
  state.rdUserProfileState.userProfileFilterValues;

/**
 * Returns selected user profile id.
 * @param userProfileState
 * @returns {number|undefined}
 */
export const getUserProfileId = (state: AppState): number | undefined =>
  state.rdUserProfileState.selectedUserProfileId;

/**
 * Returns selected user profile.
 * @param userProfileState
 * @returns {IRDUserProfileDetail|undefined}
 */
export const getSelectedUserProfile = (state: AppState): IRDUserProfileDetail =>
  state.rdUserProfileState.selectedUserProfile;

/**
 * Returns loading status of selected user profile.
 * @param userProfileState
 * @returns {boolean}
 */
export const getSelectedUserProfileIsLoadingStatus = (state: AppState): boolean =>
  state.rdUserProfileState.fetchUserProfileOperationStatus === OperationStatus.Pending;

/**
 * Returns error message returned when fetching a user profile fails.
 * @param userProfileState
 * @returns {string}
 */
export const getFetchUserProfileErrorMessage = (state: AppState): string =>
  state.rdUserProfileState.fetchUserProfileOperationStatus === OperationStatus.Failed &&
  !!state.rdUserProfileState.fetchUserProfileErrorMessage
    ? state.rdUserProfileState.fetchUserProfileErrorMessage
    : "";

/**
 * Returns user profile comments.
 * @param userProfileState
 */
export const getUserProfileComments = (state: AppState): IUserProfileComment[] =>
  state.rdUserProfileState.userProfileComments;

/**
 * Returns loading status of user profile comments.
 * @param userProfileState
 * @returns {boolean}
 */
export const getUserProfileCommentsIsLoadingStatus = (state: AppState): boolean =>
  state.rdUserProfileState.fetchUserProfileCommentsStatus === OperationStatus.Pending;

/**
 * Returns error message returned when user profile comments fails.
 * @param userProfileState
 * @returns {string}
 */
export const getUserProfileCommentsErrorMessage = (state: AppState): string =>
  state.rdUserProfileState.fetchUserProfileCommentsStatus === OperationStatus.Failed &&
  !!state.rdUserProfileState.fetchUserProfileCommentsErrorMessage
    ? state.rdUserProfileState.fetchUserProfileCommentsErrorMessage
    : "";

/**
 * Returns progress status of program profile update.
 * @param userProfileState
 * @returns {OperationStatus}
 */
export const getProgramProfileUpdateStatus = (state: AppState): OperationStatus =>
  state.rdUserProfileState.updateProgramProfileOperationStatus;

export const getProgramProfile = (state: AppState): IRDProgramProfile | undefined =>
  state.rdUserProfileState.selectedUserProfile?.programProfile;

export const getFetchProgramProfileErrorMessage = (state: AppState): string =>
  state.rdUserProfileState.fetchProgramProfileErrorMessage;

export const getUpdateProgramProfileErrorMessage = (state: AppState): string | undefined =>
  state.rdUserProfileState.updateProgramProfileErrorMessage;

/**
 * Returns in progress status of user profile comment add.
 * @param userProfileState
 * @returns {OperationStatus}
 */
export const getUserProfileAddCommentInProgressStatus = (state: AppState): OperationStatus =>
  state.rdUserProfileState.addUserProfileCommentOperationStatus;

/**
 * Returns error message returned if user profile comment add fails.
 * @param userProfileState
 * @returns {string}
 */
export const getUserProfileAddCommentErrorMessage = (state: AppState): string =>
  state.rdUserProfileState.addUserProfileCommentOperationStatus === OperationStatus.Failed &&
  !!state.rdUserProfileState.addUserProfileCommentErrorMessage
    ? state.rdUserProfileState.addUserProfileCommentErrorMessage
    : "";

/**
 * Returns the isClaimingBenefit value.
 * @param {AppState} state
 * @returns {boolean}
 */
export const getIsClaimingBenefits = (state: AppState): boolean =>
  state.rdUserProfileState.isClaimingBenefit;

/**
 * Returns the isClaimingBenefitSuccess value.
 * @param {AppState} state
 * @returns {OperationStatus}
 */
export const getIsClaimingBenefitStatus = (state: AppState): OperationStatus =>
  state.rdUserProfileState.isClaimingBenefitStatus;

/**
 * Returns the isClaimingBenefitError value.
 * @param {AppState} state
 * @returns {string}
 */
export const getIsClaimingBenefitsError = (state: AppState): string =>
  state.rdUserProfileState.claimBenefitError;

/**
 * Returns the getProfileBenefits value.
 * @param {AppState} state
 * @returns {IProfileBenefitResult}
 */
export const getProfileBenefits = (state: AppState): IProfileBenefitResult =>
  state.rdUserProfileState.profileBenefits;

/**
 * Returns the getProfileBenefitsStatus value.
 * @param {AppState} state
 * @returns {OperationStatus}
 */
export const getProfileBenefitsStatus = (state: AppState): OperationStatus =>
  state.rdUserProfileState.profileBenefitsStatus;

/**
 * Returns the getProfileBenefitsErrorMessage value.
 * @param {AppState} state
 * @returns {string}
 */
export const getProfileBenefitsErrorMessage = (state: AppState): string =>
  state.rdUserProfileState.profileBenefitsErrorMessage;

/**
 * Gets the users M365 Benefit Detail object in state.
 * @param {AppState} state
 * @returns {IM365BenefitDetail}
 */
export const m365GetBenefitDetail = (state: AppState): IM365BenefitDetail =>
  state.rdUserProfileState.m365BenefitDetailState.m365BenefitDetail;

/**
 * Gets the M365 Update Operation Status.
 * @param {AppState} state
 * @returns {OperationStatus | undefined}
 */
export const m365UpdateStatus = (state: AppState): OperationStatus | undefined =>
  state.rdUserProfileState.m365BenefitDetailState.m365OperationStatus?.update;

/**
 * Gets the M365 Fetch Operation Status.
 * @param {AppState} state
 * @returns {OperationStatus | undefined}
 */
export const m365FetchStatus = (state: AppState): OperationStatus | undefined =>
  state.rdUserProfileState.m365BenefitDetailState.m365OperationStatus?.read;

/**
 * Gets the M365 Fetch Error message from state.
 * @param {AppState} state
 * @returns {string}
 */
export const m365FetchErrorMessage = (state: AppState): string =>
  state.rdUserProfileState.m365BenefitDetailState.m365FetchErrorMessage;

/**
 * Gets the MS365 Benefit Detail from state.
 * @param {AppState} state
 * @returns {IMicrosoft365BenefitDetail}
 */
export const ms365GetBenefitDetail = (state: AppState): IMicrosoft365BenefitDetail =>
  state.rdUserProfileState.ms365BenefitDetailState.ms365BenefitDetail;

/**
 * Gets the MS365 Fetch Error message.
 * @param {AppState} state
 * @returns {string}
 */
export const ms365FetchErrorMessage = (state: AppState): string =>
  state.rdUserProfileState.ms365BenefitDetailState.ms365FetchErrorMessage;

/**
 * Gets the MS365 Update Error message
 * @param {AppState} state
 * @returns {string}
 */
export const ms365UpdateErrorMessage = (state: AppState): string =>
  state.rdUserProfileState.ms365BenefitDetailState.ms365UpdateErrorMessage;

/**
 * Gets the MS365 Fetch Operation Status.
 * @param {AppState} state
 * @returns {OperationStatus}
 */
export const ms365FetchStatus = (state: AppState): OperationStatus | undefined =>
  state.rdUserProfileState.ms365BenefitDetailState.ms365OperationStatus?.read;

/**
 * Gets the MS365 Update Operation Status.
 * @param {AppState} state
 * @returns {OperationStatus}
 */
export const ms365UpdateStatus = (state: AppState): OperationStatus | undefined =>
  state.rdUserProfileState.ms365BenefitDetailState.ms365OperationStatus?.update;

/**
 * Gets the Teams Fetch Operation Status.
 * @param {AppState} state
 * @returns {OperationStatus | undefined}
 */
export const teamsFetchStatus = (state: AppState): OperationStatus | undefined =>
  state.rdUserProfileState.teamsBenefitDetailState.teamsOperationStatus?.read;

/**
 * Gets the users Teams Benefit Detail object in state.
 * @param {AppState} state
 * @returns {ITeamsBenefitDetail}
 */
export const teamsGetBenefitDetail = (state: AppState): ITeamsBenefitDetail =>
  state.rdUserProfileState.teamsBenefitDetailState.teamsBenefitDetail;

/**
 * Gets the M365 Fetch Error message from state.
 * @param {AppState} state
 * @returns {string}
 */
export const teamsFetchErrorMessage = (state: AppState): string =>
  state.rdUserProfileState.teamsBenefitDetailState.teamsFetchErrorMessage;

/**
 * Gets the M365 Update Error Message from state.
 * @param {AppState} state
 * @returns {string}
 */
export const m365UpdateErrorMessage = (state: AppState): string =>
  state.rdUserProfileState.m365BenefitDetailState.m365UpdateErrorMessage;

export const azureBenefitUpdateStatus = (state: AppState): OperationStatus | undefined =>
  state.rdUserProfileState.azureBenefitExtendCreditState.extendAzureCreditOperationStatus?.update;

export const azureBenefitUpdateErrorMessage = (state: AppState): string =>
  state.rdUserProfileState.azureBenefitExtendCreditState.extendAzureCreditError;

/**
 * Returns update user profile is private error.
 * @param appState
 * @returns {string}
 */
export const getUserProfileUpdateIsPrivateError = (state: AppState): string =>
  state.rdUserProfileState.updateIsPrivateProfileError;

/**
 * Returns update user profile is private operation status.
 * @param appState
 * @returns {OperationStatus}
 */
export const getUserProfileUpdateIsPrivateOperationStatus = (state: AppState): OperationStatus =>
  state.rdUserProfileState.updateIsPrivateProfileStatus;

/**
 * Returns current value from state for: sortColumn
 * @param {AppState} state
 * @returns {string}
 */
export const getCurrentSortColumn = (state: AppState): string | undefined =>
  state.rdUserProfileState.sortColumn;

/**
 * Returns current value from state for: sortDescending
 * @param {AppState} state
 * @returns {boolean}
 */
export const getCurrentSortDescending = (state: AppState): boolean | undefined =>
  state.rdUserProfileState.sortDescending;

/**
 * Returns the update primary email status.
 * @param {AppState} state
 * @returns {OperationStatus}
 */
export const getUserProfileUpdatePrimaryEmailStatus = (state: AppState): OperationStatus =>
  state.rdUserProfileState.updatePrimaryEmailStatus;

/**
 * Returns the update primary email error.
 * @param {AppState} state
 * @returns {string}
 */
export const getUserProfileUpdatePrimaryEmailError = (state: AppState): string =>
  state.rdUserProfileState.updatePrimaryEmailError;

/**
 * Returns  azure benefit.
 * @param appState
 */
export const getAddAzureBenefit = (state: AppState): IRDAzureBenefitClaim | undefined =>
  state.rdUserProfileState.azureBenefitAddState.azureBenefit;

/**
 * Returns the  azure benefit.
 * @param {AppState} state
 * @returns {OperationStatus}
 */
export const getAddAzureBenefitOperationStatus = (state: AppState): IOperationStatusType =>
  state.rdUserProfileState.azureBenefitAddState.addAzureBenefitOperationStatus;

/**
 * Returns save status of azure benefit.
 * @param appState
 * @returns {boolean}
 */
export const getAddAzureBenefitStatus = (state: AppState): boolean =>
  state.rdUserProfileState.azureBenefitAddState.addAzureBenefitOperationStatus?.update ===
  OperationStatus.Pending;

/**
 * Returns error message azure benefit fails.
 * @param appState
 * @returns {string}
 */
export const getAddAzureBenefitErrorMessage = (state: AppState): string =>
  state?.rdUserProfileState?.azureBenefitAddState?.addAzureBenefitError || "";
